import { useEffect } from 'react'
import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next'
import dynamic from 'next/dynamic'
import { BogoBanner, BogoPromoModal } from '@mc/bogo'
import { TrialNoLongerAvailableModal } from '@mc/free-trial'
import { useParsePromoFeatureVariables } from '@mc/promo-utils'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS, trackPage } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { RegularFooter } from 'components/Footer/RegularFooter'
import ProfitWell from 'components/Head/ProfitWell'
import { CoreNavigation } from 'components/Navigation/consumer'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { trackPen7680BackendEV } from 'experiments/pen-7680_v1_promo_banner_gift_cta/server'
import { trackPen7694BackendEV } from 'experiments/pen-7694_v1_promo_sticky_footer_gift_cta_routing/server'
import {
  getMarketingRows,
  getFreeTrialByToken,
  getDefaultProduct,
} from 'libs/server/api'
import {
  getCourseSamplesFromGrowthAPI,
  getV2Categories,
} from 'libs/server/api/growth'
import { getPageData } from 'libs/server/page-data'
import { useSprig } from 'libs/sprig'
import { getSSRQuizOnlyOnceCookie } from 'libs/utils/quizOnlyOnce'
import { HeroQuiz } from './index/components/HeroQuiz'
import Meta from './index/components/Meta'

type HomeProps = Extract<
  InferGetServerSidePropsType<typeof getServerSideProps>,
  { status: 'success' }
>

const BelowTheFold = dynamic(() => import('./index/components/BelowTheFold'), {
  ssr: false,
})

const Home = ({
  annualPass,
  categories,
  trial,
  marketingRows,
  sampleCourses,
  ctaNextPage,
}: HomeProps) => {
  useEffect(() => {
    trackPage('Home', {
      saved_completed_quiz: !!ctaNextPage,
      platform: 'next',
    })
  }, [ctaNextPage])

  useParsePromoFeatureVariables()
  useSprig()

  return (
    <StaticDataProvider
      value={{
        annualPass,
        categories,
        trial,
        marketingRows,
        sampleCourses,
        ctaNextPage,
      }}
    >
      <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-home' }}>
        <TrialNoLongerAvailableModal />
        <PromoExplainerModal pageSource={PAGE_SOURCES.HOMEPAGE} />
        <BogoPromoModal />
        <ProfitWell />
        <Meta />
        <BogoBanner className='mc-mb-2 mc-mb-md-0' />

        <HeroQuiz />
        <BelowTheFold />
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Home.pageSettings = {
  title: 'MasterClass - Home',
  showPromo: true,
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  const ctaNextPage = await getSSRQuizOnlyOnceCookie(ctx)

  await trackPen7565BackendEV(ctx)
  await trackPen7584BackendEV(ctx)
  await trackPen7680BackendEV(ctx)
  await trackPen7694BackendEV(ctx)

  return getPageData({
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      categories: getV2Categories(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      marketingRows: getMarketingRows(ctx, {
        include: ['marketing_section_items', 'marketing_section_action'],
      }),
      sampleCourses: getCourseSamplesFromGrowthAPI(ctx),
    },
    propsMapper: (props) => ({
      ...props,
      ctaNextPage,
    }),
  })
}

export default Home
